import {
  HISTORY_DOCUMENT_CHANGE_GROUP_TYPE,
  HistoryType,
  THistDocumentChange,
  THistDocumentDataChange,
  THistDocumentFieldChange,
  THistDocumentRecordChange,
  THistory,
  THistoryDisplayItem,
  THistoryDisplayResponse,
  THistoryDocumentChangeBlock,
  THistoryDocumentChangeGroup,
  THistoryDocumentFileChange,
  THistoryResponse,
} from "../models";

const rowOperations = ["create", "delete", "change"];
const fieldTypes = [
  "text",
  "url",
  "select",
  "countrySelect",
  "date",
  "filePicker",
  "taxId",
  "sourceOfActive",
  "checkbox",
  "pep",
];

const hasOwn = (value: Record<string, unknown>, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(value, key);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const isString = (value: unknown): value is string => typeof value === "string";

const isNonEmptyString = (value: unknown): value is string =>
  isString(value) && value.length > 0;

const hasPresentValue = (value: Record<string, unknown>, key: string): boolean =>
  hasOwn(value, key) && value[key] !== undefined;

const hasIgnoredValue = (value: Record<string, unknown>, key: string): boolean =>
  !hasOwn(value, key) || value[key] === undefined || value[key] === null;

const hasIgnoredMeta = (value: Record<string, unknown>): boolean =>
  !hasOwn(value, "meta") ||
  value.meta === undefined ||
  value.meta === null ||
  (Array.isArray(value.meta) && value.meta.length === 0);

const isOptionalString = (
  value: Record<string, unknown>,
  key: string,
): boolean => !hasOwn(value, key) || value[key] === undefined || isString(value[key]);

const isKnownValue = (value: unknown, knownValues: string[]): boolean =>
  isString(value) && knownValues.indexOf(value) >= 0;

const isOptionalKnownValue = (
  value: Record<string, unknown>,
  key: string,
  knownValues: string[],
): boolean =>
  !hasOwn(value, key) ||
  value[key] === undefined ||
  value[key] === "" ||
  isKnownValue(value[key], knownValues);

const isValidValueChange = (value: unknown): boolean => {
  if (!isRecord(value) || !isString(value.operation)) {
    return false;
  }

  if (hasOwn(value, "field")) {
    return false;
  }

  switch (value.operation) {
    case "change":
      return (
        hasPresentValue(value, "before") &&
        hasPresentValue(value, "after")
      );
    case "add":
      return hasPresentValue(value, "after") && hasIgnoredValue(value, "before");
    case "delete":
      return hasPresentValue(value, "before") && hasIgnoredValue(value, "after");
    default:
      return false;
  }
};

const isValidDocumentFieldChange = (value: unknown): boolean => {
  if (
    !isRecord(value) ||
    !isNonEmptyString(value.name) ||
    !isOptionalString(value, "label")
  ) {
    return false;
  }

  if (!isOptionalKnownValue(value, "type", fieldTypes)) {
    return false;
  }

  if (
    hasOwn(value, "multiple") &&
    value.multiple !== undefined &&
    typeof value.multiple !== "boolean"
  ) {
    return false;
  }

  if (!hasOwn(value, "changes") || value.changes === undefined) {
    return true;
  }

  return (
    Array.isArray(value.changes) &&
    value.changes.every(isValidValueChange)
  );
};

const hasValidOptionalFields = (value: Record<string, unknown>): boolean => {
  if (!hasOwn(value, "fields") || value.fields === undefined) {
    return true;
  }

  return (
    Array.isArray(value.fields) &&
    value.fields.every(isValidDocumentFieldChange)
  );
};

const isValidDocumentRecordChange = (value: unknown): boolean => {
  if (!isRecord(value) || !isKnownValue(value.operation, rowOperations)) {
    return false;
  }

  if (hasOwn(value, "changes")) {
    return false;
  }

  return hasValidOptionalFields(value);
};

const isValidDocumentDataChange = (value: unknown): boolean => {
  if (
    !isRecord(value) ||
    !isKnownValue(value.operation, rowOperations) ||
    !isString(value.dataId) ||
    !isString(value.documentDataId)
  ) {
    return false;
  }

  return hasValidOptionalFields(value);
};

const isValidFileValue = (value: unknown): boolean =>
  isRecord(value) &&
  isString(value.originalName) &&
  isString(value.mimeType) &&
  isString(value.size) &&
  isOptionalString(value, "kind") &&
  isOptionalString(value, "downloadRef");

const isValidFileMetaChange = (value: unknown): boolean =>
  isRecord(value) &&
  isString(value.name) &&
  hasPresentValue(value, "before") &&
  hasPresentValue(value, "after");

const isValidDocumentFileChange = (value: unknown): boolean => {
  if (
    !isRecord(value) ||
    !isString(value.dataId) ||
    !isString(value.documentDataId) ||
    !isOptionalString(value, "fieldName") ||
    !isOptionalString(value, "fieldLabel") ||
    !isString(value.operation)
  ) {
    return false;
  }

  switch (value.operation) {
    case "upload":
      return (
        isValidFileValue(value.after) &&
        hasIgnoredValue(value, "before") &&
        hasIgnoredMeta(value)
      );
    case "delete":
      return (
        isValidFileValue(value.before) &&
        hasIgnoredValue(value, "after") &&
        hasIgnoredMeta(value)
      );
    case "replace":
      return (
        isValidFileValue(value.before) &&
        isValidFileValue(value.after) &&
        hasIgnoredMeta(value)
      );
    case "metadata_update":
      return (
        hasIgnoredValue(value, "before") &&
        hasIgnoredValue(value, "after") &&
        Array.isArray(value.meta) &&
        value.meta.length > 0 &&
        value.meta.every(isValidFileMetaChange)
      );
    default:
      return false;
  }
};

const hasValidOptionalGroup = (
  value: Record<string, unknown>,
  key: "document" | "data" | "file",
  validator: (entry: unknown) => boolean,
): boolean => {
  if (!hasOwn(value, key) || value[key] === undefined) {
    return true;
  }

  return Array.isArray(value[key]) && (value[key] as unknown[]).every(validator);
};

const groupLength = (
  value: Record<string, unknown>,
  key: "document" | "data" | "file",
): number => {
  if (!Array.isArray(value[key])) {
    return 0;
  }
  return (value[key] as unknown[]).length;
};

const isValidDocumentChange = (value: unknown): value is THistDocumentChange => {
  if (
    !isRecord(value) ||
    !isNonEmptyString(value.documentId) ||
    !isNonEmptyString(value.documentType) ||
    !isNonEmptyString(value.documentName)
  ) {
    return false;
  }

  if (
    !hasValidOptionalGroup(value, "document", isValidDocumentRecordChange) ||
    !hasValidOptionalGroup(value, "data", isValidDocumentDataChange) ||
    !hasValidOptionalGroup(value, "file", isValidDocumentFileChange)
  ) {
    return false;
  }

  return (
    groupLength(value, "document") +
      groupLength(value, "data") +
      groupLength(value, "file") >
    0
  );
};

export const getHistoryDocumentChange = (
  item: THistory,
): THistDocumentChange | null => {
  if (item.type !== HistoryType.HISTORY_TYPE_DOCUMENT_CHANGED) {
    return null;
  }

  const documentChange = item.details?.documentChange;
  if (!isValidDocumentChange(documentChange)) {
    return null;
  }

  return documentChange;
};

export const isHistoryDocumentChangeGroup = (
  item: THistoryDisplayItem,
): item is THistoryDocumentChangeGroup =>
  item.type === HISTORY_DOCUMENT_CHANGE_GROUP_TYPE;

const canGroupDocumentChange = (
  item: THistory,
): item is THistory & { userId: string } =>
  isNonEmptyString(item.userId) && getHistoryDocumentChange(item) !== null;

const hasFieldValueChanges = (
  fields: THistDocumentFieldChange[] | undefined,
): boolean => (fields ?? []).some((field) => (field.changes ?? []).length > 0);

const isDisplayableDocumentRecordChange = (
  change: THistDocumentRecordChange,
): boolean =>
  change.operation !== "create" || hasFieldValueChanges(change.fields);

const isDisplayableDataChange = (change: THistDocumentDataChange): boolean =>
  change.operation !== "create" || hasFieldValueChanges(change.fields);

const appendFilteredDocumentChange = (
  block: THistoryDocumentChangeBlock,
  item: THistory,
  change: THistDocumentChange,
): void => {
  block.sourceItemIds.push(item.id);
  block.document.push(
    ...((change.document ?? []).filter(isDisplayableDocumentRecordChange)),
  );
  block.data.push(...((change.data ?? []).filter(isDisplayableDataChange)));
  block.file.push(
    ...(change.file ?? []).map((fileChange) => ({
      ...fileChange,
      sourceHistoryId: item.id,
    })),
  );
};

const hasDisplayableBlockChanges = (
  block: THistoryDocumentChangeBlock,
): boolean => block.document.length + block.data.length + block.file.length > 0;

const buildDocumentChangeBlocks = (
  items: THistory[],
): THistoryDocumentChangeBlock[] => {
  const blocks: THistoryDocumentChangeBlock[] = [];

  items.forEach((item) => {
    const change = getHistoryDocumentChange(item);
    if (!change) {
      return;
    }

    const currentBlock = blocks[blocks.length - 1];
    const block =
      currentBlock?.documentId === change.documentId
        ? currentBlock
        : {
            documentId: change.documentId,
            documentType: change.documentType,
            documentName: change.documentName,
            sourceItemIds: [],
            document: [] as THistDocumentRecordChange[],
            data: [] as THistDocumentDataChange[],
            file: [] as THistoryDocumentFileChange[],
          };

    if (block !== currentBlock) {
      blocks.push(block);
    }

    appendFilteredDocumentChange(block, item, change);
  });

  return blocks.filter(hasDisplayableBlockChanges);
};

export const buildHistoryDisplayResponse = (
  response: THistoryResponse,
): THistoryDisplayResponse => {
  const displayItems: THistoryDisplayItem[] = [];
  let index = 0;

  while (index < response.history.length) {
    const current = response.history[index];

    if (!canGroupDocumentChange(current)) {
      displayItems.push(current);
      index += 1;
      continue;
    }

    const items = [current];
    index += 1;

    while (
      index < response.history.length &&
      canGroupDocumentChange(response.history[index]) &&
      response.history[index].userId === current.userId
    ) {
      items.push(response.history[index]);
      index += 1;
    }

    const blocks = buildDocumentChangeBlocks(items);
    if (blocks.length === 0) {
      continue;
    }

    displayItems.push({
      type: HISTORY_DOCUMENT_CHANGE_GROUP_TYPE,
      id: `document-change-group-${current.id}`,
      userId: current.userId,
      userInfo: current.userInfo,
      createdAt: current.createdAt,
      items,
      blocks,
    });
  }

  return {
    history: displayItems,
    hasMore: response.hasMore,
  };
};

export const getHistoryPaginationSince = (
  response: THistoryResponse | null,
): number | undefined => {
  const lastHistoryId = response?.history[response.history.length - 1]?.id;
  if (!lastHistoryId) {
    return undefined;
  }

  const since = parseInt(lastHistoryId, 10);
  return Number.isNaN(since) ? undefined : since;
};
